import styled from "styled-components";

import type { ColumnDefinition } from "@similarweb/ui-components";
import { ChartTooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, percentageSignFilter } from "app/filters/ngFilters";
import { CHART_HEIGHT } from "app/pages/industry-analysis/segmentation/size-and-growth-overview/common/const";
import {
    columnChartConfig,
    EChartDisplayType,
} from "pro-features/components/Chart/src/configs/column-chart/columnChartConfig";

import type { ISeries } from "../../types";

import { getCategoriesItems } from "./utils";

const TooltipWrapper = styled.div`
    padding: 16px;
    box-sizing: border-box;
`;

const getRows = (rowData: ISeries[], index: number) => {
    return rowData.map((singleRowData) => ({
        industry: {
            label: singleRowData.name,
            backgroundColor: singleRowData.color,
        },
        visits: {
            value: singleRowData.data?.[index]?.y,
        },
    }));
};

interface TooltipRow {
    industry: { label: string; backgroundColor: string };
    visits: { value: string | number };
}

const getColumnsDefinition = (isPercentage: boolean): Array<ColumnDefinition<TooltipRow>> => {
    return [
        {
            key: "1",
            title: translate("mmx.channelsoverview.channels.chart.tooltip.channel"),
            selector: "industry" as const,
            align: "left",
            size: ChartTooltip.LabelCellDefaultWidth,
            render: ({ industry }) => (
                <ChartTooltip.LabelCell
                    label={industry.label}
                    backgroundColor={industry.backgroundColor}
                />
            ),
        },
        {
            key: "2",
            title: translate("mmx.channelsoverview.channels.chart.tooltip.value"),
            selector: "visits" as const,
            align: "right",
            size: ChartTooltip.ValueAndChangeCellDefaultWidth,
            render: ({ visits }) => (
                <ChartTooltip.ValueCell
                    // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
                    value={isPercentage ? percentageSignFilter()(visits.value, 2) : abbrNumberFilter()(visits.value, 0)}
                />
            ),
        },
    ];
};

export const getChartConfig = (chartData: ISeries[] = [], isPercentage = false) => {
    const tooltipFormatter = (highChartRef) => {
        const { point } = highChartRef;
        const { index } = point;
        return (
            <TooltipWrapper>
                <ChartTooltip
                    data={getRows(chartData, index)}
                    columnDefinition={getColumnsDefinition(isPercentage)}
                    width={320}
                    title={point?.options?.key}
                />
            </TooltipWrapper>
        );
    };

    const mainSeries = chartData[0];
    if (!mainSeries) {
        return {};
    }
    const { data: mainSeriesData = [] } = mainSeries;
    const columnChartConfigArgs = {
        maxPointWidth: 40,
        height: CHART_HEIGHT,
        displayDataLabelsOnColumns: true,
        tooltipFormatter: tooltipFormatter,
        chartDisplayType: isPercentage ? EChartDisplayType.PERCENTAGE : EChartDisplayType.ABSOLUTE,
        renderCustomCategories: (index) => {
            return getCategoriesItems(mainSeriesData)[index];
        },
        tooltipConfig: {
            outside: true,
        },
    };
    return columnChartConfig(columnChartConfigArgs);
};
